import { useMemo } from 'react';
import { chrome, normalizeRoute, type NavItem } from '@/lib/content';
import { UI } from '@/lib/ui-strings';
import SmartLink from './SmartLink';
import { useLocation } from 'wouter';
import { getMedicalOfficeRoute } from '@/lib/medical-family-office';
import { EnglishOfficeFooter } from './MedicalOfficeChrome';
import GermanyFooter from './GermanyFooter';
import CountrySwitcher from './CountrySwitcher';
import { isGermanyPath } from '@/lib/markets';

/**
 * Site footer. design.md 5.2.
 *
 * Two regions: a footer body on white with a top hairline, and a legal bar on
 * the subtle surface. Every string is the one the extraction recovered; the
 * two link columns are assembled from the site's own menus, so nothing here is
 * new copy. Datensicherheit also appears in the legal bar.
 */

const SERVICE_ROUTES = [
  '/screenings',
  '/workshops-und-seminare-fuer-gesunde-mitarbeitende',
  '/genetic-sciences',
  '/epigenetic-coaching',
  '/pharmakogenetik',
];

const COMPANY_ROUTES = [
  '/referenzen',
  '/ueber-uns',
  '/datensicherheit',
  '/workplace-solutions',
  '/news',
];

const BGM_ROUTES: NavItem[] = [
  { label: 'BGM-Kennzahlen Schweiz 2026', href: '/bgm-zahlen-schweiz' },
  { label: 'Business Case BGM', href: '/business-case-bgm' },
  { label: 'Gesundheitsprogramm einführen', href: '/bgm-einfuehren' },
  { label: 'Datenschutz im BGM', href: '/datenschutz-bgm' },
];

function pick(routes: string[]): NavItem[] {
  const all: NavItem[] = [
    ...(chrome.nav.primary as NavItem[]),
    ...(chrome.nav.mobileExtra as NavItem[]),
    ...(chrome.nav.utility as NavItem[]),
  ];
  const labels = new Map(all.map((item) => [normalizeRoute(item.href), item.label]));
  const fallback: Record<string, string> = {
    '/epigenetic-coaching': 'Epigenetic Coaching',
    '/ueber-uns': 'Über Uns',
    '/referenzen': 'Referenzen',
  };
  return routes
    .map((route) => {
      const label = labels.get(route) ?? fallback[route];
      return label ? { label, href: route } : null;
    })
    .filter((item): item is NavItem => item !== null);
}

export default function Footer() {
  const [location] = useLocation();
  const officePage = getMedicalOfficeRoute(location);
  const { footer } = chrome;
  const { address } = footer;
  const phone = officePage ? '+41 44 250 28 72' : address.phone;
  const services = useMemo(() => pick(SERVICE_ROUTES), []);
  const company = useMemo(() => pick(COMPANY_ROUTES), []);
  const legal = footer.legal as NavItem[];
  // The legal bar carries the data-protection surface, nothing else from the
  // utility menu: it is where a procurement reader scans for it (5.2).
  const legalExtra = (chrome.nav.utility as NavItem[]).filter(
    (item) => normalizeRoute(item.href) === '/datensicherheit',
  );

  if (isGermanyPath(location)) return <GermanyFooter />;
  if (officePage?.language === 'en') return <EnglishOfficeFooter />;

  return (
    <footer className="hp-footer" id="footer-kontakt">
      <div className="hp-footer__body">
        <div className="hp-container hp-footer__inner">
          <div className="hp-footer__col hp-footer__col--brand">
            <SmartLink to="/" className="hp-footer__brand" aria-label={UI.toHome}>
              <img
                className="hp-footer__logo"
                src={chrome.brand.logo}
                alt={chrome.brand.name}
                width={778}
                height={124}
                loading="lazy"
                decoding="async"
              />
            </SmartLink>
            <address className="hp-footer__address">
              <span className="hp-footer__company">{address.company}</span>
              <span className="hp-footer__street">{address.street}</span>
              <span className="hp-footer__city">{address.city}</span>
              <a className="hp-footer__contact-link" href={`mailto:${address.email}`}>
                {address.email}
              </a>
              <a
                className="hp-footer__contact-link"
                href={`tel:${phone.replace(/\s+/g, '')}`}
              >
                {phone}
              </a>
              <a className="hp-footer__contact-link" href={chrome.contact.whatsapp} target="_blank" rel="noopener noreferrer">
                WhatsApp Chat
              </a>
            </address>
            <p className="hp-footer__about">{footer.aboutText}</p>
            {officePage ? <p className="hp-footer__about">Health Prime Medical Family Office<br />Ein Department der Health Prime AG</p> : null}
          </div>

          <nav className="hp-footer__col hp-footer__col--links" aria-labelledby="hp-footer-services">
            <h2 className="hp-footer__title hp-h6" id="hp-footer-services">
              {UI.footerServices}
            </h2>
            <ul className="hp-footer__links">
              <li><SmartLink to="/executive-medical-office" className="hp-footer__link">Executive Medical Office</SmartLink></li>
              <li><SmartLink to="/private-medical-office" className="hp-footer__link">Private Medical Office</SmartLink></li>
              {services.map((item) => (
                <li className="hp-footer__link-item" key={item.href}>
                  <SmartLink to={item.href} className="hp-footer__link">
                    {item.label}
                  </SmartLink>
                </li>
              ))}
            </ul>
          </nav>

          <nav className="hp-footer__col hp-footer__col--links" aria-labelledby="hp-footer-company">
            <h2 className="hp-footer__title hp-h6" id="hp-footer-company">
              {UI.footerCompany}
            </h2>
            <ul className="hp-footer__links">
              {company.map((item) => (
                <li className="hp-footer__link-item" key={item.href}>
                  <SmartLink to={item.href} className="hp-footer__link">
                    {item.label}
                  </SmartLink>
                </li>
              ))}
            </ul>
          </nav>

          <nav
            className="hp-footer__col hp-footer__col--links"
            aria-labelledby="hp-footer-bgm"
          >
            <h2 className="hp-footer__title hp-h6" id="hp-footer-bgm">
              Facts & Figures
            </h2>
            <ul className="hp-footer__links">
              {BGM_ROUTES.map((item) => (
                <li className="hp-footer__link-item" key={item.href}>
                  <SmartLink to={item.href} className="hp-footer__link">
                    {item.label}
                  </SmartLink>
                </li>
              ))}
            </ul>
            <CountrySwitcher />
          </nav>
        </div>
      </div>

      <div className="hp-footer__legal">
        <div className="hp-container hp-footer__legal-inner">
          <p className="hp-footer__copyright">
            © 2013 - {new Date().getFullYear()} Health Prime AG
          </p>
          <nav aria-label={UI.legalNav}>
            <ul className="hp-footer__legal-links">
              {[...legal, ...legalExtra].map((item) => (
                <li key={item.href}>
                  <SmartLink to={item.href} className="hp-footer__legal-link">
                    {item.label}
                  </SmartLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </footer>
  );
}
